"use client";

import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useSession } from "@atalan/auth/react";
import { api } from "@/lib/api";
import { handlePromiseRejection, loadUnreadMessagesCount } from "@/lib/messageCenter/actions";
import { useClient, useMessageServiceActions, useUnreadMessages } from "@/store/messageService";
interface MessageCenterContextProps {
  unreadMessagesQty: number;
}
const MessageCenterContext = createContext<MessageCenterContextProps | undefined>(undefined);
interface MessageCenterProviderProps {
  children: ReactNode;
}
export function MessageCenterProvider({
  children
}: MessageCenterProviderProps) {
  const userRef = useRef<boolean>(false);
  const initTokenRef = useRef<boolean>(false);
  const {
    data: session,
    status
  } = useSession();
  const {
    setToken,
    setClient,
    setCurrentUserId,
    setConnectionState,
    upsertConversation,
    updateUnreadMessages
  } = useMessageServiceActions();
  const user = session?.user;
  const utils = api.useUtils();
  const {
    data: token
  } = api.messageCenter.getOrCreateToken.useQuery(undefined, {
    enabled: status === "authenticated" && !!user
  });
  const client = useClient();
  const unreadMessages = useUnreadMessages();
  const [unreadMessagesQty, setUnreadMessagesQty] = useState(0);
  useEffect(() => {
    if (user && !userRef.current) {
      setCurrentUserId(user.id);
      userRef.current = true;
    }
  }, [setCurrentUserId, user]);
  useEffect(() => {
    if (token && !initTokenRef.current) {
      setToken(token);
      setClient(token);
      initTokenRef.current = true;
    }
  }, [setClient, setToken, token]);
  useEffect(() => {
    if (client) {
      client.on("conversationJoined", conversation => {
        handlePromiseRejection(async () => {
          if (conversation.status === "joined") {
            await loadUnreadMessagesCount(conversation, updateUnreadMessages);
          }
        });
      });
      client.on("tokenAboutToExpire", () => {
        if (token) {
          client.updateToken(token);
          setToken(token);
        }
      });
      client.on("tokenExpired", () => {
        if (user) {
          utils.messageCenter.getOrCreateToken.fetch().then(newToken => {
            setToken(newToken);
            setClient(newToken);
          });
        }
      });
      client.on("connectionStateChanged", state => {
        setConnectionState(state);
      });
      return () => {
        client.removeListener("conversationJoined", conversation => {
          handlePromiseRejection(async () => {
            if (conversation.status === "joined") {
              await loadUnreadMessagesCount(conversation, updateUnreadMessages);
            }
          });
        });
        client.removeListener("tokenAboutToExpire", () => {
          if (token) {
            client.updateToken(token);
            setToken(token);
          }
        });
        client.removeListener("tokenExpired", () => {
          if (user) {
            utils.messageCenter.getOrCreateToken.fetch().then(newToken => {
              setToken(newToken);
              setClient(newToken);
            });
          }
        });
        client.removeListener("connectionStateChanged", state => {
          setConnectionState(state);
        });
      };
    }
  }, [client, setClient, setConnectionState, setToken, token, upsertConversation, user, utils, updateUnreadMessages]);
  useEffect(() => {
    const sum = Object.values(unreadMessages).reduce((acc, value) => acc + value, 0);
    setUnreadMessagesQty(sum);
    return () => new AbortController().abort();
  }, [unreadMessages]);
  return <MessageCenterContext.Provider value={{
    unreadMessagesQty
  }}>
      {children}
    </MessageCenterContext.Provider>;
}
export function useMessageCenterContext() {
  const context = useContext(MessageCenterContext);
  if (context === undefined) {
    throw new Error("useMessageCenterContext must be used within a MessageCenterProvider");
  }
  return context;
}